import React from 'react'
import { useKeycloak } from '@react-keycloak/web'
import { KeycloakHelper } from '@monorepo/infra'

/**
 * Groups allowed to use the admin application.
 *
 * Anyone whose Keycloak token does NOT contain at least one of these groups
 * will see an "Access denied" screen after login and be offered a Sign-out
 * button. This prevents non-admin users (dealers, Test accounts, etc.) from
 * entering the admin UI even if they successfully authenticate against the
 * shared Keycloak realm.
 *
 * Note: backend endpoints enforce their own per-feature group checks on top
 * of this; this guard is an additional, UX-level layer. Keep this list in
 * sync with product expectations — removing a group here locks that group
 * out of the entire admin app.
 */
const ADMIN_ALLOWED_GROUPS = [
  'directvMarketing',
  'eventVendor',
  'eventVendorAdmin',
  'productVendor',
  'productVendorAdmin',
  'superAdmin',
  'vendor',
  'vendorAdmin',
  'zoner',
]

/** Keycloak sometimes prefixes group names with '/' — match either form. */
const allowedRegexes = ADMIN_ALLOWED_GROUPS.map(
  (g) => new RegExp(`^\\/?${g}$`, 'i')
)

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function userIsAllowed(parsedToken: any): boolean {
  const groups = parsedToken?.groups
  if (!Array.isArray(groups)) return false
  try {
    return groups.some((g: string) =>
      allowedRegexes.some((r) => r.test(g))
    )
  } catch {
    return false
  }
}

interface AdminAccessGuardProps {
  children: React.ReactNode
}

const AdminAccessGuard: React.FC<AdminAccessGuardProps> = ({ children }) => {
  const { keycloak, initialized } = useKeycloak()

  // Before Keycloak finishes initialising or for unauthenticated users,
  // defer to the normal flow — PrivateRoute will redirect to the login page.
  if (!initialized || !keycloak.authenticated) {
    return <>{children}</>
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const parsed = KeycloakHelper.getTokenParsed(keycloak) as any

  if (userIsAllowed(parsed)) {
    return <>{children}</>
  }

  return <AccessDeniedScreen onSignOut={() => keycloak.logout()} />
}

interface AccessDeniedScreenProps {
  onSignOut: () => void
}

const AccessDeniedScreen: React.FC<AccessDeniedScreenProps> = ({ onSignOut }) => (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      background: '#fafafa',
      textAlign: 'center',
    }}
  >
    <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#111' }}>
      Access denied
    </h1>
    <p
      style={{
        fontSize: '0.95rem',
        color: '#555',
        maxWidth: '28rem',
        lineHeight: 1.5,
        marginBottom: '1.5rem',
      }}
    >
      Your account doesn&apos;t have permission to access the admin panel. If
      you believe this is a mistake, contact your administrator.
    </p>
    <button
      type="button"
      onClick={onSignOut}
      style={{
        padding: '0.6rem 1.5rem',
        borderRadius: 6,
        border: 'none',
        background: '#111',
        color: '#fff',
        fontSize: '0.9rem',
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      Sign out
    </button>
  </div>
)

export default AdminAccessGuard
